import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Quote, ArrowRight, Anchor } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Initial({ name }) {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="w-16 h-16 rounded-full bg-pink-soft text-navy font-serif-display text-2xl flex items-center justify-center shrink-0">
      {letter}
    </div>
  );
}

export default function MemberSpotlight() {
  const [items, setItems] = useState(null); // null = loading, [] = empty
  const [active, setActive] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/spotlights`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  // Auto-rotate
  useEffect(() => {
    if (!items || items.length < 2) return;
    const t = setInterval(() => setActive((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items]);

  if (items === null) return null; // loading

  // EMPTY STATE — turn this slot into a Captain's Club conversion CTA
  if (items.length === 0) {
    return (
      <section
        className="bg-navy text-white py-24"
        data-testid="member-spotlight-empty"
      >
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 text-center">
          <Anchor size={26} strokeWidth={1.5} className="text-pink-soft mx-auto mb-6" />
          <div className="overline text-pink-soft mb-4">The Captain's Club</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto">
            Be among the first to take a permanent seat at our table.
          </h2>
          <p className="text-white/70 mt-6 max-w-xl mx-auto leading-relaxed">
            Our inaugural members shape the Club's traditions for years to come — exclusive
            sails, member-only events, and a standing welcome at the dock. Membership is
            capped at 60 to keep the circle close.
          </p>
          <Link
            to="/captains-club"
            className="inline-flex items-center gap-2 bg-pink-soft text-navy px-7 py-4 mt-10 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-white transition-colors"
            data-testid="spotlight-empty-cta"
          >
            Apply to Join <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  const current = items[active];

  return (
    <section className="bg-navy text-white py-24" data-testid="member-spotlight">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <div className="overline text-pink-soft mb-8">Member Spotlight</div>
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2">
            {current.photo_url ? (
              <img
                src={current.photo_url}
                alt={current.name}
                className="w-32 h-32 rounded-full object-cover"
                data-testid="spotlight-photo"
              />
            ) : (
              <Initial name={current.name} />
            )}
          </div>
          <div className="lg:col-span-10">
            <Quote size={28} className="text-pink-soft mb-4" strokeWidth={1.5} />
            <p
              className="font-serif-display text-3xl lg:text-4xl leading-snug text-white"
              data-testid="spotlight-quote"
            >
              "{current.quote}"
            </p>
            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-serif-display text-xl" data-testid="spotlight-name">
                {current.name}
              </span>
              {current.tier && (
                <span className="text-[0.7rem] tracking-[0.22em] uppercase text-pink-soft">
                  · {current.tier} · The Captain's Club
                </span>
              )}
            </div>
          </div>
        </div>

        {items.length > 1 && (
          <div className="flex items-center gap-2 mt-12" data-testid="spotlight-dots">
            {items.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-1.5 transition-all ${
                  i === active ? "w-10 bg-pink-soft" : "w-4 bg-white/30 hover:bg-white/50"
                }`}
                aria-label={`Show spotlight ${i + 1}`}
                data-testid={`spotlight-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
